import React, { useCallback, useEffect, useMemo } from 'react'
import { Box } from '@hub/box'
import { theme } from '@hub/design-system-base/src/theme'
import { H5 } from '@hub/heading'
import { Stack } from '@hub/stack'
import { Form } from '@scentregroup/shared/hub-components/form'
import { Button, ButtonLink, ButtonStack } from '../button'
import useNotification from '../../lib/use-notification'
import { useProfileEdit } from '../../lib/use-profile-edit'
import { ProfileSection } from '../../types/profile'

type ProfileLayoutProps = {
  section: ProfileSection
  title: string
  readOnlyView: React.ReactNode
  handleSubmit: (
    e: React.ChangeEvent<HTMLButtonElement> | React.FormEvent<HTMLElement>
  ) => void
  handleCancel: () => void
  hasChanges: boolean
  isSubmitting?: boolean
  children: React.ReactNode
}

const ProfileLayout: React.FC<React.PropsWithChildren<ProfileLayoutProps>> = ({
  section,
  title,
  readOnlyView,
  handleSubmit,
  handleCancel,
  hasChanges,
  isSubmitting = false,
  children,
}) => {
  const { closeNotification } = useNotification()

  const { edit, cancelEdit, isInReadOnly, isInEdit, isAnotherSectionInEdit } =
    useProfileEdit(section)

  const testId = useMemo(
    () => `profile-section-${title.toLowerCase().replace(/ /g, '-')}`,
    [title]
  )

  useEffect(() => {
    // We want to close previous notifications when entering edit mode
    if (isAnotherSectionInEdit) {
      closeNotification()
    }
  }, [isAnotherSectionInEdit, closeNotification])

  const onCancel = useCallback(() => {
    handleCancel()
    cancelEdit()
  }, [handleCancel, cancelEdit])

  if (isAnotherSectionInEdit) {
    return null
  }

  return (
    <Box
      marginBottom={'spacing-md'}
      paddingBottom={'spacing-md'}
      borderBottom={`1px solid ${theme.colors.ghost}`}
      gap={'spacing-sm'}
      data-test-id={testId}
    >
      {isInReadOnly && (
        <>
          <Stack direction={'row'} justify={'space-between'}>
            <H5>{title}</H5>
            <ButtonLink onClick={edit}>Edit</ButtonLink>
          </Stack>
          {readOnlyView}
        </>
      )}
      {isInEdit && (
        <Form gap={'spacing-lg'} onSubmit={e => handleSubmit(e)}>
          {children}
          <ButtonStack>
            <Button
              type={'submit'}
              isFullWidth
              isDisabled={!hasChanges}
              isLoading={isSubmitting}
            >
              Save
            </Button>
            <Button variant={'outline'} isFullWidth onClick={onCancel}>
              Cancel
            </Button>
          </ButtonStack>
        </Form>
      )}
    </Box>
  )
}

export default ProfileLayout
